import React, { useCallback, useContext, useMemo } from "react"
import {
  Box,
  ButtonBase,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Breakpoint
} from "@mui/material"
import {
  EllipsisTooltip,
  Tooltip
} from "../../../../mp-shared-components/ready-shared-components/components"
import { FaIcon } from "../../../../mp-core/icons"
import {
  getSortIconName,
  selectSorting,
  transformDiagonalHeaderStyle
} from "../helpers"
import TableStyleContext from "../TableStyleContext"
import { SortHandler, SortTypes } from "../types"
import TableCell from "./TableCell"

type Props = {
  label: React.ReactNode
  name: string
  sortableName?: string
  tooltip?: React.ReactNode
  sortable?: boolean
  sortBy: string
  order: SortTypes
  onSort: SortHandler
  className?: string
  diagonalHeader?: boolean
  isFirstDiagonalHeader?: boolean
  hasDiagonalHeaders?: boolean
  colSpan?: number
  rowSpan?: number
  style?: React.CSSProperties
}

const TableHeadCell: React.FC<Props> = ({
  label,
  name,
  sortableName,
  tooltip,
  sortable,
  sortBy: currentSortBy,
  order: currentOrder,
  onSort,
  className,
  diagonalHeader = false,
  isFirstDiagonalHeader = false,
  hasDiagonalHeaders,
  ...rest
}) => {
  const { classes, cx } = useContext(TableStyleContext)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md" as Breakpoint))

  const columnSortableName = sortableName || name
  const isActiveSort = currentSortBy === columnSortableName
  const currentAriaSort = isActiveSort
    ? currentOrder === SortTypes.asc
      ? "ascending"
      : "descending"
    : "none"

  const handleCellSort = useCallback(
    (name: string) => () => {
      const { sortBy, order } = selectSorting(name, currentSortBy, currentOrder)
      onSort?.(sortBy, order)
    },
    [onSort, currentSortBy, currentOrder]
  )

  const transformStyle = useMemo(
    () => transformDiagonalHeaderStyle(diagonalHeader, isMobile, rest.style),
    [diagonalHeader, rest.style, isMobile]
  )

  if (diagonalHeader) {
    return (
      <TableCell
        role="columnheader"
        aria-sort={sortable ? currentAriaSort : undefined}
        key={columnSortableName}
        className={cx(classes.diagonalHeaderCell, {
          [classes.sortable]: sortable
        })}
        data-automation-id="data-table-head-cell"
        onClick={sortable ? handleCellSort(columnSortableName) : undefined}
        sx={
          sortable
            ? {
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "action.hover"
                }
              }
            : undefined
        }
        {...rest}
      >
        <Box className={classes.diagonalHeaderInner} style={transformStyle}>
          <Box
            component="span"
            className={cx(classes.diagonalHeaderText, {
              ["first-diagonal-column"]: isFirstDiagonalHeader
            })}
          >
            <EllipsisTooltip title={label} type="data" placement="bottom">
              <Typography
                variant="caption"
                component="p"
                className={classes.diagonalHeaderLabel}
                data-automation-id="data-table-column-title"
              >
                {label}
              </Typography>
            </EllipsisTooltip>
            {sortable && (
              <Box component="span" className={classes.diagonalHeaderSortIcon}>
                <FaIcon
                  size="regular"
                  solid
                  aria-hidden="true"
                  name={getSortIconName(
                    currentSortBy,
                    currentOrder,
                    columnSortableName
                  )}
                  className={cx(
                    classes.sortIcon,
                    currentSortBy === columnSortableName &&
                      classes.activeSortIcon
                  )}
                  data-automation-id="data-table-column-sort-icon"
                />
              </Box>
            )}
          </Box>
        </Box>
      </TableCell>
    )
  }

  const cellContentElement = (
    <Stack spacing={2} direction="row" display="flex" alignItems="center">
      <Typography
        variant="subtitle1"
        component="p"
        data-automation-id="data-table-column-title"
      >
        {label}
      </Typography>
      {sortable && (
        <FaIcon
          size="regular"
          solid
          aria-hidden="true"
          name={getSortIconName(
            currentSortBy,
            currentOrder,
            columnSortableName
          )}
          className={cx(
            classes.sortIcon,
            currentSortBy === columnSortableName && classes.activeSortIcon
          )}
          data-automation-id="data-table-column-sort-icon"
        />
      )}
    </Stack>
  )

  const wrapperContent = useMemo(() => {
    if (sortable) {
      return (
        <ButtonBase
          disableRipple
          className={cx(className, classes.tableCell, classes.sortButton, {
            [classes.tableCellDiagonal]: hasDiagonalHeaders
          })}
          onClick={handleCellSort(columnSortableName)}
        >
          {cellContentElement}
        </ButtonBase>
      )
    }

    if (!sortable && hasDiagonalHeaders) {
      return (
        <Box
          className={cx(
            className,
            classes.tableCell,
            classes.tableCellDiagonal
          )}
        >
          {cellContentElement}
        </Box>
      )
    }

    return cellContentElement
  }, [
    sortable,
    hasDiagonalHeaders,
    className,
    cellContentElement,
    handleCellSort,
    columnSortableName
  ])

  return (
    <Tooltip
      title={tooltip}
      showTooltip={!!tooltip}
      type="data"
      placement="bottom"
    >
      <TableCell
        role="columnheader"
        key={columnSortableName}
        aria-sort={sortable ? currentAriaSort : undefined}
        className={cx(className, classes.tableCell, classes.headTableCell, {
          [classes.sortable]: sortable,
          [classes.headTableCellDiagonal]: hasDiagonalHeaders
        })}
        data-automation-id="data-table-head-cell"
        {...rest}
      >
        {wrapperContent}
      </TableCell>
    </Tooltip>
  )
}

export default TableHeadCell
